import { useEffect, useLayoutEffect } from "react";
import { useJumpUrl } from "../../service-axios";
import { usePushID } from "../../service-pushid";

// API of the useEmbed custom hook:
const useDefaultHook = () => ({
  // Loading state
  loading: false,
  loaded: true,

  // Provide error management
  error: null,

  // Provide the data to associated with the page's API
  scope: "*",
  params: {},
  events: {}
});

export const useHostPage = (receivedUrl = "", hook = useDefaultHook) => {
  const idRef = { current: usePushID() };

  const {
    loading: hookIsLoading = false,
    loaded: hookHasLoaded = true,
    error: hookError = null,
    url = receivedUrl,
    scope = "*",
    params = {},
    events = {}
  } = hook(receivedUrl) || {};

  const hookInfo = {
    loading: hookIsLoading,
    loaded: hookHasLoaded,
    error: hookError
  };

  // Encode the params into the URL component
  const paramsObject = {
    ...(params || {}),
    embed: "yes",
    embedID: idRef.current,
    embedScope: scope
  };
  const paramsString = Object.keys(paramsObject)
    .reduce(
      (acc, key) => [
        ...acc,
        [encodeURIComponent(key), encodeURIComponent(paramsObject[key])].join(
          "="
        )
      ],
      []
    )
    .join("&");

  // Calculate the final embed url that needs to be resolved
  const embedUrl = `${url}${url.includes("?") ? "&" : "?"}${paramsString}`;

  const frameInfo = useJumpUrl(embedUrl, { lazy: true });

  // Await for the hookInfo to resolve
  // then start resolving the embed page target url
  useEffect(() => {
    if (!hookInfo.loaded) return;
    frameInfo.fetch();
  }, [hookInfo.loaded]);

  useLayoutEffect(() => {
    if (!frameInfo.loaded) return;

    // console.log("@useHostPage: attach events handlers");
    const handleMessage = (evt) => {
      try {
        const { embedID, name, data } = JSON.parse(evt.data);

        if (!name) {
          console.error('Event payload has no "name" property');
          return;
        }

        if (embedID !== String(idRef.current)) return;
        events[name] && events[name](data);
      } catch (err) {}
    };

    // Attach the event handler:
    window.addEventListener("message", handleMessage, false);
    return () => window.removeEventListener("message", handleMessage, false);
  }, [frameInfo.loaded]);

  return {
    ...frameInfo,
    loading: hookInfo.loading || frameInfo.loading,
    loaded: hookInfo.loaded || frameInfo.loaded,
    error: hookInfo.error || frameInfo.error
  };
};
